import * as React from "react";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { MuiTextFieldType } from "../../common/muiFieldTypes/muiTextFieldType";
import { handleCheckBoxClick } from "./CameraAutoAddUtils/handleCheckboxClick";
import { handleImmixEmailInput } from "./CameraAutoAddUtils/handleImmixEmailInput";
import { handleImmixInput1Input } from "./CameraAutoAddUtils/handleImmixInput1Input";
import { handleDiceEmailInput } from "./CameraAutoAddUtils/handleDiceEmailInput";
import { handleDiceInput1Input } from "./CameraAutoAddUtils/handleDiceInput1Input";
import { CircularProgress, Typography, TablePagination } from "@mui/material";
import { handleSelectAllClick } from "./CameraAutoAddUtils/handleSelectAllClick";
import { handleAllCamerasImmixEmailInput } from "./CameraAutoAddUtils/handleAllCamerasImmixEmailInput";
import { handleSureviewEmailInput } from "./CameraAutoAddUtils/handelSureViewEmailInput";
import { handleSureviewCCInput } from "./CameraAutoAddUtils/handleSureViewCCInput";
import { additionalFieldTableCellGenerator } from "./CameraAutoAddUtils/additionalFieldTableCellGenerator";
import { Link, useNavigate } from "react-router-dom";
import { MuiCheckBoxFieldType } from "../../common/muiFieldTypes/muiCheckBoxFieldType";
import { handleSentinelInput } from "./CameraAutoAddUtils/handleSentinelInput";
import { handleEvalinkInput } from "./CameraAutoAddUtils/handleEvalinkInput";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { useCameraAutoAddStore } from "./CameraAutoAddStore/cameraAutoAddStore";
import { handleLoadMoreAutoAddData } from "./CameraAutoAddUtils/handleLoadMorAutoAddData";
import { getBlockedProductIdsForSelectedIds } from "../../common/onBoardingWizardFieldTypes/cameraProductsMutualExclusion";
import { productsMatcher } from "../../utils/productsMatcher";
import { MenuItem, TextField } from "@mui/material";
import { hasMovedRecordingServer } from "./CameraAutoAddUtils/handleFixCameras";
import {
  genetecStreamOptions,
  genetecStreamValue,
  handleGenetecStreamInput,
  hasGenetecStreamOptions,
  GenetecStreamOption,
} from "./CameraAutoAddUtils/genetecStreamPicker";

// Memoized table row component to prevent unnecessary re-renders
const TableRowMemo = React.memo(
  ({
    obj,
    selectedProducts,
    additionalFieldNamesArray,
    checkboxesChecked,
    setCheckboxesChecked,
    setCamerasObject,
    camerasObject,
    productsArray,
    immixSite,
    diceSite,
    sureViewSite,
    evalinkSite,
    largerScreen,
    genetecStreamSite,
  }: {
    obj: any;
    selectedProducts: any;
    additionalFieldNamesArray: any;
    checkboxesChecked: any;
    setCheckboxesChecked: any;
    setCamerasObject: any;
    camerasObject: any;
    productsArray: any[];
    immixSite: any;
    diceSite: any;
    sureViewSite: any;
    evalinkSite: any;
    largerScreen: boolean;
    genetecStreamSite?: boolean;
  }) => {
    const immixAlertPostObject = JSON.parse(localStorage.immixAlertPostObject);
    if (immixAlertPostObject.immix_server) {
      camerasObject[obj.id].immix.immix_server =
        immixAlertPostObject.immix_server;
    }
    if (immixAlertPostObject.immix_port) {
      camerasObject[obj.id].immix.immix_port = immixAlertPostObject.immix_port;
    }

    const diceAlertPostObject = JSON.parse(
      localStorage.diceAlertPostObject || "{}"
    );
    if (diceAlertPostObject.dice_server) {
      camerasObject[obj.id].dice.dice_server = diceAlertPostObject.dice_server;
    }
    if (diceAlertPostObject.dice_port) {
      camerasObject[obj.id].dice.dice_port = diceAlertPostObject.dice_port;
    }

    const sentinelAlertPostObject = JSON.parse(
      localStorage.sentinelAlertPostObject
    );
    if (sentinelAlertPostObject.sentinel_server) {
      camerasObject[obj.id].sentinel.sentinel_server =
        sentinelAlertPostObject.sentinel_server;
    }
    if (sentinelAlertPostObject.sentinel_site_id) {
      camerasObject[obj.id].sentinel.sentinel_site_id =
        sentinelAlertPostObject.sentinel_site_id;
    }
    if (sentinelAlertPostObject.sentinel_device_address) {
      camerasObject[obj.id].sentinel.sentinel_device_address =
        sentinelAlertPostObject.sentinel_device_address;
    }
    if (sentinelAlertPostObject.draw_ignore_zones) {
      camerasObject[obj.id].sentinel.draw_ignore_zones =
        sentinelAlertPostObject.draw_ignore_zones;
    }

    const patriotAlertPostObject = JSON.parse(
      localStorage.patriotAlertPostObject || "{}"
    );
    if (patriotAlertPostObject.patriot_server) {
      camerasObject[obj.id].patriot.patriot_server =
        patriotAlertPostObject.patriot_server;
    }
    if (patriotAlertPostObject.patriot_client_no) {
      camerasObject[obj.id].patriot.patriot_client_no =
        patriotAlertPostObject.patriot_client_no;
    }
    if (patriotAlertPostObject.patriot_username) {
      camerasObject[obj.id].patriot.patriot_username =
        patriotAlertPostObject.patriot_username;
    }
    if (patriotAlertPostObject.patriot_password) {
      camerasObject[obj.id].patriot.patriot_password =
        patriotAlertPostObject.patriot_password;
    }
    if (patriotAlertPostObject.patriot_zone_number) {
      camerasObject[obj.id].patriot.patriot_zone_number =
        patriotAlertPostObject.patriot_zone_number;
    }
    if (patriotAlertPostObject.patriot_type_number) {
      camerasObject[obj.id].patriot.patriot_type_number =
        patriotAlertPostObject.patriot_type_number;
    }
    if (patriotAlertPostObject.patriot_send_video) {
      camerasObject[obj.id].patriot.patriot_send_video =
        patriotAlertPostObject.patriot_send_video;
    }
    if (patriotAlertPostObject.draw_ignore_zones) {
      camerasObject[obj.id].patriot.draw_ignore_zones =
        patriotAlertPostObject.draw_ignore_zones;
    }

    // Evalink site-level fields are shared across cameras; alarm_zone_id is
    // per camera and intentionally not seeded (each camera's zone must be
    // entered deliberately — ENG-352).
    const evalinkAlertPostObject = JSON.parse(
      localStorage.evalinkAlertPostObject || "{}"
    );
    if (evalinkAlertPostObject.evalink_token) {
      camerasObject[obj.id].evalink.evalink_token =
        evalinkAlertPostObject.evalink_token;
    }
    if (evalinkAlertPostObject.evalink_device_id) {
      camerasObject[obj.id].evalink.evalink_device_id =
        evalinkAlertPostObject.evalink_device_id;
    }
    if (evalinkAlertPostObject.evalink_company_id) {
      camerasObject[obj.id].evalink.evalink_company_id =
        evalinkAlertPostObject.evalink_company_id;
    }
    if (evalinkAlertPostObject.partition_id) {
      camerasObject[obj.id].evalink.partition_id =
        evalinkAlertPostObject.partition_id;
    }
    return obj.camera ? (
      <React.Fragment key={obj.id}>
        <TableRow>
          <TableCell sx={{ fontFamily: "Mulish" }}>
            {obj.camera_current_name &&
            obj.camera_current_name !== obj.camera_name ? (
              // The camera was renamed on the NVR: show the deployed (current)
              // name struck through, then the new incoming name. Applying it is
              // done via "Fix Camera Names" (keeps the camera id / device UID).
              <div>
                <div className="text-[#0000EE] hover:text-actuate-orange line-through">
                  <Link
                    to={`/sites/${localStorage.siteId}/editcamera/${obj.camera}`}
                    target="_blank"
                  >
                    {obj.camera_current_name}
                  </Link>
                </div>
                <div className="text-[12px] text-gray-500">New name:</div>
                <div>{obj.camera_name}</div>
              </div>
            ) : (
              <div className="text-[#0000EE] hover:text-actuate-orange">
                <Link
                  to={`/sites/${localStorage.siteId}/editcamera/${obj.camera}`}
                  target="_blank"
                >
                  {obj.camera_name}
                </Link>
              </div>
            )}
            {hasMovedRecordingServer(obj) && (
              <div className="text-[12px] text-gray-500 italic">
                Old server: {obj.camera_current_recording_server}
              </div>
            )}
          </TableCell>
          <TableCell sx={{ fontFamily: "Mulish" }}>{obj.nvr_id}</TableCell>
          {additionalFieldNamesArray && additionalFieldNamesArray.length > 0 ? (
            additionalFieldNamesArray.map((object: any) => {
              return (
                <TableCell key={object.id} sx={{ fontFamily: "Mulish" }}>
                  {additionalFieldTableCellGenerator(
                    object.type,
                    object.id,
                    obj,
                    largerScreen
                  )}
                </TableCell>
              );
            })
          ) : (
            <></>
          )}
          {genetecStreamSite && (
            <TableCell sx={{ fontFamily: "Mulish" }}>--</TableCell>
          )}
          {selectedProducts.map((productName: any) => {
            const name = `${productName}${obj.id}`;
            return (
              <React.Fragment key={productName}>
                <TableCell>
                  <></>
                </TableCell>
              </React.Fragment>
            );
          })}
          {immixSite && (
            <TableCell>
              <></>
            </TableCell>
          )}
          {immixSite && (
            <TableCell>
              <></>
            </TableCell>
          )}
          {sureViewSite && (
            <TableCell>
              <></>
            </TableCell>
          )}
          {sureViewSite && (
            <TableCell>
              <></>
            </TableCell>
          )}
          {diceSite && (
            <TableCell>
              <></>
            </TableCell>
          )}
          {diceSite && (
            <TableCell>
              <></>
            </TableCell>
          )}
          {evalinkSite && (
            <TableCell>
              <></>
            </TableCell>
          )}
        </TableRow>
      </React.Fragment>
    ) : (
      <React.Fragment key={obj.id}>
        <TableRow>
          <TableCell sx={{ fontFamily: "Mulish" }}>{obj.camera_name}</TableCell>
          <TableCell sx={{ fontFamily: "Mulish" }}>{obj.nvr_id}</TableCell>
          {additionalFieldNamesArray && additionalFieldNamesArray.length > 0 ? (
            additionalFieldNamesArray.map((object: any) => {
              return (
                <TableCell key={object.id} sx={{ fontFamily: "Mulish" }}>
                  {additionalFieldTableCellGenerator(
                    object.type,
                    object.id,
                    obj,
                    largerScreen
                  )}
                </TableCell>
              );
            })
          ) : (
            <></>
          )}
          {genetecStreamSite && (
            <TableCell sx={{ fontFamily: "Mulish" }}>
              {genetecStreamOptions(obj).length > 0 ? (
                <TextField
                  id={`genetecStream${obj.id}`}
                  select
                  variant="standard"
                  sx={{ minWidth: 240 }}
                  inputProps={{ "aria-label": `Stream for ${obj.camera_name}` }}
                  value={genetecStreamValue(camerasObject, obj.id)}
                  onChange={(event: any) =>
                    handleGenetecStreamInput(
                      setCamerasObject,
                      event.target.value,
                      obj.id
                    )
                  }
                >
                  {genetecStreamOptions(obj).map(
                    ([label, value]: GenetecStreamOption) => (
                      <MenuItem key={value || "auto"} value={value}>
                        {label}
                      </MenuItem>
                    )
                  )}
                </TextField>
              ) : (
                "Auto"
              )}
            </TableCell>
          )}
          {selectedProducts.map((productName: any) => {
            const name = `${productName}${obj.id}`;
            const selectedProductIds: number[] =
              camerasObject?.[obj.id]?.products || [];
            const productId = productsMatcher("Id", productName, productsArray);
            const disabledByConflict =
              productId !== undefined &&
              productId !== null &&
              getBlockedProductIdsForSelectedIds(
                selectedProductIds,
                productsArray
              ).has(productId);
            return (
              <React.Fragment key={productName}>
                <TableCell>
                  <FormControlLabel
                    sx={{ fontFamily: "Mulish" }}
                    control={
                      <Checkbox
                        checked={Boolean(checkboxesChecked[name])}
                        color="primary"
                        disabled={disabledByConflict}
                        onClick={(event: any) => {
                          setCheckboxesChecked((previousState: any) => {
                            return {
                              ...previousState,
                              [name]: event.target.checked,
                            };
                          });
                          handleCheckBoxClick(
                            productName,
                            setCamerasObject,
                            camerasObject,
                            obj.id,
                            event.target.checked,
                            productsArray
                          );
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: "Mulish",
                          fontSize: "14px",
                        }}
                      >
                        {productName}
                      </Typography>
                    }
                  />
                </TableCell>
              </React.Fragment>
            );
          })}
          {immixSite && (
            <TableCell>
              <MuiTextFieldType
                id="immixPortInput"
                defaultValue=""
                value=""
                onChange={(event: any) => {
                  handleImmixEmailInput(
                    setCamerasObject,
                    camerasObject,
                    event.target.value,
                    obj.id
                  );
                }}
                helperText=""
                stylingWidth={200}
                label="Immix Email"
                error={false}
              />
            </TableCell>
          )}
          {immixSite && (
            <TableCell>
              <MuiTextFieldType
                id="immixInput1"
                defaultValue=""
                value=""
                onChange={(event: any) => {
                  handleImmixInput1Input(
                    setCamerasObject,
                    camerasObject,
                    event.target.value,
                    obj.id
                  );
                }}
                helperText=""
                stylingWidth={100}
                label="Immix input 1"
                error={false}
              />
            </TableCell>
          )}
          {sureViewSite && (
            <TableCell>
              <MuiTextFieldType
                id="sureViewEmail"
                defaultValue=""
                value=""
                onChange={(event: any) => {
                  handleSureviewEmailInput(
                    setCamerasObject,
                    camerasObject,
                    event.target.value,
                    obj.id
                  );
                }}
                helperText=""
                stylingWidth={200}
                label="Sureview Email"
                error={false}
              />
            </TableCell>
          )}
          {sureViewSite && (
            <TableCell>
              <MuiTextFieldType
                id="sureViewCC"
                defaultValue=""
                value=""
                onChange={(event: any) => {
                  handleSureviewCCInput(
                    setCamerasObject,
                    camerasObject,
                    event.target.value,
                    obj.id
                  );
                }}
                helperText=""
                stylingWidth={200}
                label="Sureview cc"
                error={false}
              />
            </TableCell>
          )}
          {diceSite && (
            <TableCell>
              <MuiTextFieldType
                id="diceEmail"
                defaultValue=""
                value=""
                onChange={(event: any) => {
                  handleDiceEmailInput(
                    setCamerasObject,
                    camerasObject,
                    event.target.value,
                    obj.id
                  );
                }}
                helperText=""
                stylingWidth={200}
                label="Dice Email"
                error={false}
              />
            </TableCell>
          )}
          {diceSite && (
            <TableCell>
              <MuiTextFieldType
                id="diceInput1"
                defaultValue=""
                value=""
                onChange={(event: any) => {
                  handleDiceInput1Input(
                    setCamerasObject,
                    camerasObject,
                    event.target.value,
                    obj.id
                  );
                }}
                helperText=""
                stylingWidth={100}
                label="Dice input 1"
                error={false}
              />
            </TableCell>
          )}
          {evalinkSite && (
            <TableCell>
              <MuiTextFieldType
                id="evalinkAlarmZoneId"
                defaultValue=""
                value=""
                onChange={(event: any) => {
                  handleEvalinkInput(
                    setCamerasObject,
                    camerasObject,
                    event.target.value,
                    obj.id,
                    "alarmZoneId"
                  );
                }}
                helperText=""
                stylingWidth={150}
                label="Alarm Zone ID"
                error={false}
              />
            </TableCell>
          )}
        </TableRow>
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    // Re-render if the row object itself changed
    if (prevProps.obj !== nextProps.obj) {
      return false;
    }

    // Re-render if selected products changed
    // Optimize: only re-render if the array contents actually changed, not just the reference
    if (prevProps.selectedProducts !== nextProps.selectedProducts) {
      // Check if arrays have different lengths
      if (
        prevProps.selectedProducts.length !== nextProps.selectedProducts.length
      ) {
        return false;
      }
      // Check if any product was added or removed
      const prevSet = new Set(prevProps.selectedProducts);
      const nextSet = new Set(nextProps.selectedProducts);
      if (prevSet.size !== nextSet.size) {
        return false;
      }
      for (const product of prevSet) {
        if (!nextSet.has(product)) {
          return false;
        }
      }
      // Arrays have same contents, skip re-render
      return true;
    }

    // Re-render if other important props changed
    if (
      prevProps.additionalFieldNamesArray !==
        nextProps.additionalFieldNamesArray ||
      prevProps.immixSite !== nextProps.immixSite ||
      prevProps.diceSite !== nextProps.diceSite ||
      prevProps.sureViewSite !== nextProps.sureViewSite ||
      prevProps.evalinkSite !== nextProps.evalinkSite ||
      prevProps.largerScreen !== nextProps.largerScreen ||
      prevProps.genetecStreamSite !== nextProps.genetecStreamSite
    ) {
      return false;
    }

    // Check if camerasObject changed for this specific row
    const rowId = prevProps.obj.id;
    if (prevProps.camerasObject[rowId] !== nextProps.camerasObject[rowId]) {
      return false;
    }

    // Most importantly: check if any checkbox values for this specific row have changed
    // Only check the checkboxes relevant to this row, not the entire object
    const rowIdStr = String(rowId);
    for (const productName of prevProps.selectedProducts) {
      const checkboxName = `${productName}${rowIdStr}`;
      const prevChecked = Boolean(prevProps.checkboxesChecked[checkboxName]);
      const nextChecked = Boolean(nextProps.checkboxesChecked[checkboxName]);
      if (prevChecked !== nextChecked) {
        return false; // Checkbox value changed, should re-render
      }
    }

    // If checkboxesChecked object reference changed, only re-render if values for this row changed
    // This is the key optimization - we don't re-render if other rows' checkboxes changed
    if (prevProps.checkboxesChecked !== nextProps.checkboxesChecked) {
      // Check if any checkbox for this specific row changed
      for (const productName of prevProps.selectedProducts) {
        const checkboxName = `${productName}${rowIdStr}`;
        if (
          prevProps.checkboxesChecked[checkboxName] !==
          nextProps.checkboxesChecked[checkboxName]
        ) {
          return false; // This row's checkbox changed
        }
      }
      // If reference changed but this row's values are the same, skip re-render
      return true;
    }

    return true; // No changes detected, skip re-render
  }
);

TableRowMemo.displayName = "TableRowMemo";

export const AutoAddTable = ({
  camerasList,
  selectedProducts,
  camerasObject,
  setCamerasObject,
  checkboxesChecked,
  setCheckboxesChecked,
  additionalFieldNamesArray,
  immixSite,
  diceSite,
  sureViewSite,
  productsArray,
  sentinelSite,
  patriotSite,
  evalinkSite,
  setCamerasList,
  largerScreen,
  hanwhaSite,
}: {
  camerasList: any;
  selectedProducts: any;
  camerasObject: any;
  setCamerasObject: any;
  checkboxesChecked: any;
  setCheckboxesChecked: any;
  additionalFieldNamesArray: any;
  immixSite: any;
  diceSite?: any;
  sureViewSite: any;
  productsArray: any[];
  sentinelSite: any;
  patriotSite?: any;
  evalinkSite?: any;
  setCamerasList: any;
  largerScreen: boolean;
  hanwhaSite?: any;
}) => {
  const {
    moreDataToLoadAutoAdd,
    setMoreDataToLoadAutoAdd,
    loadingMoreAutoAddData,
    setLoadingMoreAutoAddData,
    columnSpan,
    setColumnSpan,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCamerasPerAPIResponse,
  } = useCameraAutoAddStore();

  // Genetec sites with a stream probe get a per-camera Stream picker (ENG-2043).
  const genetecStreamSite = hasGenetecStreamOptions(camerasList?.results);

  React.useEffect(() => {
    // Base columns (camera name + device id + spacing) plus one per selected
    // product. Each alarm type that renders per-camera columns adds two
    // (email + input1/cc); Evalink adds one (alarm zone id); the Genetec
    // Stream picker adds one.
    let span = 4 + selectedProducts.length + (genetecStreamSite ? 1 : 0);
    if (immixSite) {
      span += 2;
    }
    if (sureViewSite) {
      span += 2;
    }
    if (diceSite) {
      span += 2;
    }
    if (evalinkSite) {
      span += 1;
    }
    setColumnSpan(span);
  }, [
    selectedProducts,
    immixSite,
    sureViewSite,
    diceSite,
    evalinkSite,
    genetecStreamSite,
    setColumnSpan,
  ]);

  // Reset to first page if current page is out of bounds
  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(camerasList.results.length / rowsPerPage) - 1
    );
    if (page > maxPage) {
      setPage(0);
    }
  }, [camerasList.results.length, rowsPerPage, page, setPage]);

  const navigate = useNavigate();

  // Memoize the header checkbox handler to prevent unnecessary re-renders
  const handleHeaderCheckboxChange = React.useCallback(
    (productName: string) => {
      return (event: any) => {
        const checked = event.target.checked;
        setCheckboxesChecked((previousState: any) => {
          return {
            ...previousState,
            [productName]: checked,
          };
        });
        handleSelectAllClick(
          camerasObject,
          setCamerasObject,
          productName,
          checked,
          checkboxesChecked,
          setCheckboxesChecked,
          productsArray
        );
      };
    },
    [
      camerasObject,
      setCamerasObject,
      checkboxesChecked,
      setCheckboxesChecked,
      productsArray,
    ]
  );

  // Memoize the table head to prevent unnecessary re-renders
  const EnhancedTableHead = React.useMemo(() => {
    return (
      <TableHead>
        <TableRow>
          <TableCell
            sx={{ fontFamily: "Mulish", fontWeight: "Bold", minWidth: 200 }}
          >
            Camera Name
          </TableCell>
          <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
            Device ID
          </TableCell>
          {additionalFieldNamesArray && additionalFieldNamesArray.length > 0 ? (
            additionalFieldNamesArray.map((object: any) => {
              return (
                <TableCell
                  key={object.id}
                  sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}
                >
                  {object.label}
                </TableCell>
              );
            })
          ) : (
            <></>
          )}
          {genetecStreamSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              Stream
            </TableCell>
          )}
          {selectedProducts.map((productName: string) => {
            return (
              <React.Fragment key={productName}>
                <TableCell padding="checkbox">
                  <FormControlLabel
                    sx={{ fontFamily: "Mulish", width: 135 }}
                    control={
                      <Checkbox
                        checked={checkboxesChecked[productName]}
                        color="primary"
                        onChange={handleHeaderCheckboxChange(productName)}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: "Mulish",
                          fontSize: "14px",
                          fontWeight: "Bold",
                        }}
                      >
                        {productName}
                      </Typography>
                    }
                  />
                </TableCell>
              </React.Fragment>
            );
          })}
          {immixSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              {/* <MuiTextFieldType
                    id="immixPortInput"
                    defaultValue=""
                    value=""
                    onChange={(event: any) => {
                      handleAllCamerasImmixEmailInput(
                        camerasObject,
                        setCamerasObject,
                        event.target.value,
                      );
                    }}
                    helperText="Applies to all cameras"
                    stylingWidth={200}
                    label="Immix Email"
                    error={false}
                  /> */}
              Immix Email
            </TableCell>
          )}
          {immixSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              {`Immix input 1 (optional)`}
            </TableCell>
          )}
          {sureViewSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              {`Sureview Email`}
            </TableCell>
          )}
          {sureViewSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              {`Sureview cc`}
            </TableCell>
          )}
          {diceSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              Dice Email
            </TableCell>
          )}
          {diceSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              {`Dice input 1 (optional)`}
            </TableCell>
          )}
          {evalinkSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              Evalink Alarm Zone ID
            </TableCell>
          )}
          {/* {sentinelSite && (
            <TableCell sx={{ fontFamily: "Mulish", fontWeight: "Bold" }}>
              {`Draw Ignore Zones`}
            </TableCell>
          )} */}
        </TableRow>
      </TableHead>
    );
  }, [
    additionalFieldNamesArray,
    selectedProducts,
    checkboxesChecked,
    handleHeaderCheckboxChange,
    immixSite,
    diceSite,
    sureViewSite,
    evalinkSite,
    genetecStreamSite,
  ]);

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedResults = camerasList.results.slice(startIndex, endIndex);
  const isLastPage = endIndex >= camerasList.results.length;
  const shouldShowLoadMore = moreDataToLoadAutoAdd && isLastPage;

  // Memoize table rows to prevent unnecessary re-renders
  // Note: We include checkboxesChecked and camerasObject in dependencies
  // because rows need to reflect their current state, but React.memo on TableRowMemo
  // will prevent individual rows from re-rendering unless their specific props change
  const tableRows = React.useMemo(() => {
    return paginatedResults.map((obj: any) => (
      <TableRowMemo
        key={obj.id}
        obj={obj}
        selectedProducts={selectedProducts}
        additionalFieldNamesArray={additionalFieldNamesArray}
        checkboxesChecked={checkboxesChecked}
        setCheckboxesChecked={setCheckboxesChecked}
        setCamerasObject={setCamerasObject}
        camerasObject={camerasObject}
        productsArray={productsArray}
        immixSite={immixSite}
        diceSite={diceSite}
        sureViewSite={sureViewSite}
        evalinkSite={evalinkSite}
        largerScreen={largerScreen}
        genetecStreamSite={genetecStreamSite}
      />
    ));
  }, [
    paginatedResults,
    selectedProducts,
    additionalFieldNamesArray,
    // Use a more stable reference check - only recreate if the object keys/values for visible rows changed
    checkboxesChecked,
    camerasObject,
    productsArray,
    immixSite,
    diceSite,
    sureViewSite,
    evalinkSite,
    largerScreen,
    genetecStreamSite,
    setCheckboxesChecked,
    setCamerasObject,
  ]);

  const handleChangePage = React.useCallback(
    (event: unknown, newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleChangeRowsPerPage = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [setRowsPerPage, setPage]
  );

  const shouldLimitHeight =
    immixSite ||
    diceSite ||
    sureViewSite ||
    sentinelSite ||
    patriotSite ||
    evalinkSite ||
    hanwhaSite;

  return (
    <>
      <div
        style={
          shouldLimitHeight
            ? // Alarm-integration sites render extra config controls below the
              // table (server/port, ignore-zone toggle, etc.), so cap the table
              // and give it its own scroll to keep those reachable. Use a
              // viewport-relative height (not a fixed 350px) so the table grows
              // with the window instead of stranding rows on tall screens; the
              // 340px reserves the nav/toolbar above and the config block below.
              {
                maxHeight: "calc(100vh - 340px)",
                minHeight: "350px",
                overflowY: "auto",
              }
            : {}
        }
      >
        <Table stickyHeader aria-label="sticky table">
          {EnhancedTableHead}
          <TableBody>
            {tableRows}
            {shouldShowLoadMore && (
              <TableRow>
                <TableCell colSpan={columnSpan}>
                  <div className="flex justify-center">
                    {loadingMoreAutoAddData ? (
                      <CircularProgress color="primary" />
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-center text-sm text-gray-500">
                          {" "}
                          {camerasList.results.length} cameras loaded of{" "}
                          {totalCamerasPerAPIResponse} total cameras
                        </p>
                        <OrangeTextButton
                          onClick={() => {
                            setLoadingMoreAutoAddData(true);
                            handleLoadMoreAutoAddData(
                              setCamerasList,
                              setCamerasObject,
                              navigate,
                              productsArray,
                              setCheckboxesChecked,
                              checkboxesChecked
                            );
                          }}
                          text={
                            totalCamerasPerAPIResponse -
                              camerasList.results.length >=
                            100
                              ? "Load Next 100 Cameras"
                              : `Load Next ${
                                  totalCamerasPerAPIResponse -
                                  camerasList.results.length
                                } Cameras`
                          }
                          disabled={false}
                        />
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={camerasList.results.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};
